/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import { IFilterSelectBox } from "../../Shared/Filter/types";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { setRegionFilterIds, setSelectedTaskType, setSelectedTaskStatus } from "../../../store/filters/tasks/reducers";
import { taskFilterSelector } from "../../../store/filters/tasks/selectors";
import { useEffect, useMemo } from "react";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { IGeoRegion } from "../../../store/api/admin/types";
import { customTasks } from "../../../globals/constants";

export const useTasksFilter = () => {
  const taskFilterData = useSelector(taskFilterSelector);
  const dispatch = useStoreDispatch();
  const { geoRegions } = useRegionDisplay();
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);

  const regionOptions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [];

    const baseRegions = selectedGeoRegionIds && selectedGeoRegionIds.length
      ? geoRegions.filter((region: IGeoRegion) => selectedGeoRegionIds.includes(region.objectKey))
      : geoRegions;

    return baseRegions
      .map((region: IGeoRegion) => ({ value: region.objectKey, text: region.name }))
      .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
  }, [geoRegions, selectedGeoRegionIds]);

  // Synchronize local region filter with global region filter changes
  useEffect(() => {
    if (!selectedGeoRegionIds || selectedGeoRegionIds.length === 0) return;
    if (!taskFilterData.regionFilterIds || taskFilterData.regionFilterIds.length === 0) return;

    // Filter out any regions that are no longer in the global selection
    const validRegions = taskFilterData.regionFilterIds.filter((regionId: string) =>
      selectedGeoRegionIds.includes(regionId)
    );

    // If some regions were filtered out, update the local filter
    if (validRegions.length !== taskFilterData.regionFilterIds.length) {
      dispatch(setRegionFilterIds(validRegions));
    }
  }, [selectedGeoRegionIds, taskFilterData.regionFilterIds, dispatch]);

  // Get task type values from customTasks (same source as task creation dialog)
  const taskTypeValues = useMemo(() => {
    const defaultTaskTypes = ["Database Backup", "Database Integrity"];
    return Array.from(new Set([...Object.values(customTasks), ...defaultTaskTypes]))
      .sort((a, b) => a.localeCompare(b));
  }, []);

  const filters: IFilterSelectBox[] = [
    {
      id: "regionFilter",
      label: "REGION",
      value: taskFilterData.regionFilterIds || [],
      setValue: setRegionFilterIds,
      values: regionOptions,
      width: "200px",
      multiple: true,
    },
    {
      id: "taskType",
      label: "TYPE",
      value: taskFilterData.selectedTaskType || "",
      setValue: setSelectedTaskType,
      values: taskTypeValues,
      width: "250px",
    },
    {
      id: "taskStatus",
      label: "STATUS",
      value: taskFilterData.selectedTaskStatus || "",
      setValue: setSelectedTaskStatus,
      values: ["ABORTED", "FAILED", "FINISHED", "IDLE", "RUNNING", "SERVER_DOWN", "STOPPED", "SUCCESSFUL", "SUSPENDED", "TIMEOUT"],
      width: "150px",
    },
  ];

  const formControlSx: SxProps = { minWidth: 100, marginRight: "10px" };

  return {
    dispatch,
    filters,
    formControlSx,
  };
};
